import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCall } from "../../utils/api";

const initialState = {
  data: [],
  userHistory: [],
  suggestionQueries: [],
  summary: null,
  summaryStatus: "idle",
  topUsers: null,
  topUsersStatus: "idle",
  topQueries: null,
  topQueriesStatus: "idle",
  usersByType: null,
  usersByTypeStatus: "idle",
  engagementSummary: null,
  engagementSummaryStatus: "idle",
  error: "",
  status: "idle",
};

export const getSearchReports = createAsyncThunk(
  "searchReports/get",
  async ({ query = "", page = 1, per_page = 50, exclude_suggestions = false, user_filter = "all", date = "", unique_queries = false }) => {
    try {
      let url = `/admin/v1/search-reports?query=${query}&page=${page}&per_page=${per_page}&exclude_suggestions=${exclude_suggestions}&user_filter=${user_filter}&unique_queries=${unique_queries}`;
      if (date) {
        url += `&date=${date}`;
      }
      const response = await getCall(url, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getUserSearchHistory = createAsyncThunk(
  "searchReports/getUserHistory",
  async ({ userId, page = 1, per_page = 50, exclude_suggestions = false }) => {
    try {
      const response = await getCall(
        `/admin/v1/search-reports/user/${userId}?page=${page}&per_page=${per_page}&exclude_suggestions=${exclude_suggestions}`,
        false
      );
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getSuggestionQueries = createAsyncThunk(
  "searchReports/getSuggestionQueries",
  async () => {
    try {
      const response = await getCall(
        `/admin/v1/search-reports/suggestion-queries`,
        false
      );
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getSearchSummary = createAsyncThunk(
  "searchReports/getSummary",
  async ({ start_date, end_date, exclude_suggestions = false } = {}) => {
    try {
      let url = `/admin/v1/search-reports/summary`;
      const params = [];
      if (start_date) params.push(`start_date=${start_date}`);
      if (end_date) params.push(`end_date=${end_date}`);
      params.push(`exclude_suggestions=${exclude_suggestions}`);
      if (params.length > 0) url += `?${params.join("&")}`;

      const response = await getCall(url, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getTopUsers = createAsyncThunk(
  "searchReports/getTopUsers",
  async ({ start_date, end_date, exclude_suggestions = false, limit = 50 } = {}) => {
    try {
      const params = new URLSearchParams({
        exclude_suggestions,
        limit,
      });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);

      const response = await getCall(`/admin/v1/search-reports/top-users?${params}`, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getTopQueries = createAsyncThunk(
  "searchReports/getTopQueries",
  async ({ start_date, end_date, exclude_suggestions = true, limit = 100 } = {}) => {
    try {
      const params = new URLSearchParams({
        exclude_suggestions,
        limit,
      });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);

      const response = await getCall(`/admin/v1/search-reports/top-queries?${params}`, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getUsersByType = createAsyncThunk(
  "searchReports/getUsersByType",
  async ({ start_date, end_date, type = "all", exclude_suggestions = false, limit = 50 } = {}) => {
    try {
      const params = new URLSearchParams({
        type,
        exclude_suggestions,
        limit,
      });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);

      const response = await getCall(`/admin/v1/search-reports/users-by-type?${params}`, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getEngagementSummary = createAsyncThunk(
  "searchReports/getEngagementSummary",
  async ({ start_date, end_date, exclude_suggestions = false } = {}) => {
    try {
      const params = new URLSearchParams({
        exclude_suggestions,
      });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);

      const response = await getCall(`/admin/v1/search-reports/engagement-summary?${params}`, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

const searchReportsSlice = createSlice({
  name: "searchReports",
  initialState,
  reducers: {
    clearUserHistory: (state) => {
      state.userHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Get search reports
      .addCase(getSearchReports.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSearchReports.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(getSearchReports.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      // Get user search history
      .addCase(getUserSearchHistory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserSearchHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userHistory = action.payload;
      })
      .addCase(getUserSearchHistory.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      // Get suggestion queries
      .addCase(getSuggestionQueries.fulfilled, (state, action) => {
        state.suggestionQueries = action.payload?.data?.queries || [];
      })
      // Get search summary
      .addCase(getSearchSummary.pending, (state) => {
        state.summaryStatus = "loading";
      })
      .addCase(getSearchSummary.fulfilled, (state, action) => {
        state.summaryStatus = "succeeded";
        state.summary = action.payload?.data;
      })
      .addCase(getSearchSummary.rejected, (state, action) => {
        state.summaryStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      // Get top users
      .addCase(getTopUsers.pending, (state) => {
        state.topUsersStatus = "loading";
      })
      .addCase(getTopUsers.fulfilled, (state, action) => {
        state.topUsersStatus = "succeeded";
        state.topUsers = action.payload?.data;
      })
      .addCase(getTopUsers.rejected, (state, action) => {
        state.topUsersStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      // Get top queries
      .addCase(getTopQueries.pending, (state) => {
        state.topQueriesStatus = "loading";
      })
      .addCase(getTopQueries.fulfilled, (state, action) => {
        state.topQueriesStatus = "succeeded";
        state.topQueries = action.payload?.data;
      })
      .addCase(getTopQueries.rejected, (state, action) => {
        state.topQueriesStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      // Get users by type
      .addCase(getUsersByType.pending, (state) => {
        state.usersByTypeStatus = "loading";
      })
      .addCase(getUsersByType.fulfilled, (state, action) => {
        state.usersByTypeStatus = "succeeded";
        state.usersByType = action.payload?.data;
      })
      .addCase(getUsersByType.rejected, (state, action) => {
        state.usersByTypeStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      // Get engagement summary
      .addCase(getEngagementSummary.pending, (state) => {
        state.engagementSummaryStatus = "loading";
      })
      .addCase(getEngagementSummary.fulfilled, (state, action) => {
        state.engagementSummaryStatus = "succeeded";
        state.engagementSummary = action.payload?.data;
      })
      .addCase(getEngagementSummary.rejected, (state, action) => {
        state.engagementSummaryStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      });
  },
});

export const { clearUserHistory } = searchReportsSlice.actions;
export default searchReportsSlice.reducer;
